// Curation rules for the public Community page (PANDA-1265).
//
// The Community page is a shop window: it should only show projects whose
// author handle, title and source account look intentional. These helpers
// drop placeholder handles ("firstname"), keyboard-mash / random strings
// ("sdlkjlkjlkj", "asdasd"), obvious test accounts and junk titles before a
// card is ever rendered. They are deliberately conservative — the goal is to
// remove the obvious junk without hiding real users who happen to have an
// unusual handle.

export function normalizeName(name?: string | null): string {
  return (name || "").trim().toLowerCase();
}

// Exact handles that are clearly placeholder / test content.
const PLACEHOLDER_NAMES = new Set([
  "firstname",
  "firsname",
  "lastname",
  "fullname",
  "full name",
  "name",
  "yourname",
  "your name",
  "username",
  "user",
  "test",
  "tester",
  "testing",
  "testuser",
  "demo",
  "demouser",
  "example",
  "sample",
  "admin",
  "administrator",
  "placeholder",
  "unknown",
  "anonymous",
  "guest",
  "null",
  "undefined",
  "asdf",
  "qwerty",
  "foo",
  "bar",
  "baz",
  "lorem",
  "ipsum",
  "abc",
  "abcd",
  "xyz",
  "john doe",
  "jane doe",
  "dummy",
]);

// Handles that START with an obvious placeholder / test token. Covers the long
// tail like "testuser5456", "test1", "firstname2", "demo-app", "user_ltqz",
// "userremix", "dummytest".
const PLACEHOLDER_PREFIXES = [
  "firstname",
  "firsname",
  "lastname",
  "test",
  "demo",
  "dummy",
  "placeholder",
  "sample",
  "example",
  "user",
];

// Known internal QA handles. These are throwaway builds from the team's own
// test accounts (e.g. "mitulbidx", "mitulpublish", "mohanabcd", "talha") that
// repeatedly leak onto the public page. Matched as a prefix. Keep this list
// short and obvious — it is intentionally a manual blocklist, not a heuristic.
const INTERNAL_TESTER_PREFIXES = ["mitul", "mohan", "talha"];

const VOWELS = /[aeiou]/g;

// Detects keyboard-mash / random-looking handles such as "sdlkjlkjlkj",
// "asdasd", "msdsdsdsd", "sdfsdfsdfsd", "jy_w344". Real handles — even unusual
// ones like "blancochicano" or "kingflashythegamer" — have vowels, varied
// characters and no repeated unit, so they pass through.
export function looksLikeRandomString(name?: string | null): boolean {
  const s = normalizeName(name).replace(/[\s._-]/g, "");
  if (s.length < 5) return false;
  // Anything with non-alphanumerics left (e.g. a real "First Last") is treated
  // as intentional.
  if (!/^[a-z0-9]+$/.test(s)) return false;

  // The whole string is a short unit repeated: "asdasd", "testtest", "lolol".
  if (/^(.{1,4})\1{1,}$/.test(s)) return true;

  const letters = s.replace(/[0-9]/g, "");
  const digits = s.length - letters.length;
  // Digit-heavy handle: "jy_w344", "user1234".
  if (digits >= 3 && digits / s.length >= 0.4) return true;

  const vowels = (letters.match(VOWELS) || []).length;
  // No vowels at all in a 5+ letter run: "sdlkjlkj".
  if (letters.length >= 5 && vowels === 0) return true;
  // Almost no vowels: "sdfssdfs".
  if (letters.length >= 6 && vowels / letters.length < 0.2) return true;
  // Long consonant cluster: keyboard mash.
  if (/[bcdfghjklmnpqrstvwxz]{5,}/.test(letters)) return true;
  // Very low character diversity for the length: "asasasadass".
  const distinct = new Set(s).size;
  if (s.length >= 6 && distinct <= Math.ceil(s.length * 0.4)) return true;

  return false;
}

export function isPlaceholderAuthorName(name?: string | null): boolean {
  const n = normalizeName(name);
  if (!n) return true;
  // Single character / empty after stripping separators.
  if (n.replace(/[\s._-]/g, "").length <= 1) return true;
  if (n === "unknown") return true;
  if (PLACEHOLDER_NAMES.has(n)) return true;
  if (PLACEHOLDER_PREFIXES.some((p) => n.startsWith(p))) return true;
  if (INTERNAL_TESTER_PREFIXES.some((p) => n.startsWith(p))) return true;
  return false;
}

// Generic test-account email detection. Runs server-side only. Matches the
// usual throwaway domains and local-part conventions (+test tags, "qa", "e2e",
// "playwright", etc.).
// `yopmail` is listed by name because QA uses it routinely and it was NOT
// matched here: only `qa@yopmail.com`-style addresses were caught, via the
// local-part rule below, so a plain `someone@yopmail.com` counted as a real
// user in the funnels and the failure dashboard. `.fr`/`.net` are the same
// service under different TLDs and are used interchangeably.
const TEST_EMAIL_DOMAINS =
  /@(example\.(com|org|net)|test\.(com|org)|mailinator\.com|sharklasers\.com|guerrillamail\.com|yopmail\.(com|fr|net)|.*\.local)$/i;
const TEST_EMAIL_LOCALPART =
  /(^|[._+-])(test|tests|testing|qa|e2e|demo|sample|dummy|fake|staging|playwright|automation|bot|noreply|no-reply)(\d+)?([._+-]|$)/i;

export function isTestAccountEmail(email?: string | null): boolean {
  if (!email) return false;
  const e = email.trim().toLowerCase();
  if (TEST_EMAIL_DOMAINS.test(e)) return true;
  const local = e.split("@")[0] || "";
  if (TEST_EMAIL_LOCALPART.test(local)) return true;
  return false;
}

// Titles that read as filler / unfinished.
const BAD_TITLES = new Set([
  "untitled",
  "untitled project",
  "untitled app",
  "new project",
  "my project",
  "my app",
  "test",
  "test project",
  "test app",
  "demo",
  "example",
  "app",
  "project",
]);

export function meetsTitleQualityBar(title?: string | null): boolean {
  const t = (title || "").trim();
  if (t.length < 3) return false;
  if (BAD_TITLES.has(t.toLowerCase())) return false;
  return true;
}

export interface CommunityCurationInput {
  authorName?: string | null;
  email?: string | null;
  title?: string | null;
}

// Single entry point: returns true only when a project is presentable on the
// public Community page.
export function shouldShowInCommunity({
  authorName,
  email,
  title,
}: CommunityCurationInput): boolean {
  if (isTestAccountEmail(email)) return false;
  if (isPlaceholderAuthorName(authorName)) return false;
  if (looksLikeRandomString(authorName)) return false;
  if (!meetsTitleQualityBar(title)) return false;
  return true;
}
